// src/utils/errorHandler.js

/**
 * HTTP status from an axios error after either raw rejection or `formatApiError` wrapping.
 * Use this instead of `error.response?.status` when the error may have passed through api interceptors.
 */
export function getHttpStatusFromApiError(error) {
  if (!error) return undefined;
  return (
    error.response?.status ??
    error.statusCode ??
    error.originalError?.response?.status
  );
}

const DEFAULT_FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again later.';

/** True when axios `formatApiError` already captured a usable message from `response.data`. */
function hasUsableFormattedMessage(formattedMessage) {
  return typeof formattedMessage === 'string' && formattedMessage.trim().length > 0;
}

/**
 * After axios interceptors reject with {@link formatApiError}, callers often lack `error.response`.
 * Prefer the original response body fields, then the formatted object's `details` and `message`.
 *
 * @param {unknown} error - Raw axios error or formatted error from {@link formatApiError}
 * @param {string} fallback - Final fallback when nothing else is usable
 * @returns {string}
 */
export function getApiErrorMessageFromCatch(error, fallback) {
  const fromResponseBody =
    (typeof error?.response?.data?.error === 'string' && error.response.data.error.trim()) ||
    (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim());

  const fromDetails =
    (typeof error?.details?.error === 'string' && error.details.error.trim()) ||
    (typeof error?.details?.message === 'string' && error.details.message.trim());

  const fromFormattedMessage =
    typeof error?.message === 'string' && error.message.trim() ? error.message.trim() : '';

  return fromResponseBody || fromDetails || fromFormattedMessage || fallback;
}

/**
 * Formats error messages from API responses into a standardized format
 * @param {Error} error - The error object from the API request
 * @returns {Object} Standardized error object
 */
export const formatApiError = (error) => {
    // Default error structure
    const formattedError = {
      message: DEFAULT_FALLBACK_MESSAGE,
      statusCode: null,
      type: 'UNKNOWN_ERROR',
      details: null,
      originalError: error
    };
  
    // Handle axios error objects which have response property
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      formattedError.statusCode = error.response.status;
      
      // Get the error message from the response if available
      let extractedFromBody = false;
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          formattedError.message = error.response.data;
          extractedFromBody = true;
        } else if (error.response.data.message) {
          formattedError.message = error.response.data.message;
          extractedFromBody = true;
        } else if (error.response.data.error) {
          formattedError.message = error.response.data.error;
          extractedFromBody = true;
        }
        
        // Set any additional details from the response
        formattedError.details = error.response.data;
      }

      const useGenericUnlessServerMessage =
        !extractedFromBody || !hasUsableFormattedMessage(formattedError.message);
      
      // Categorize error based on status code
      if (error.response.status >= 400 && error.response.status < 500) {
        if (error.response.status === 401) {
          formattedError.type = 'AUTHENTICATION_ERROR';
          // Only use generic message if no specific backend message exists
          if (!hasUsableFormattedMessage(formattedError.message) || formattedError.message === DEFAULT_FALLBACK_MESSAGE) {
            formattedError.message = 'Authentication failed. Please log in again.';
          }
        } else if (error.response.status === 403) {
          formattedError.type = 'AUTHORIZATION_ERROR';
          if (useGenericUnlessServerMessage) {
            formattedError.message = 'You do not have permission to perform this action.';
          }
        } else if (error.response.status === 404) {
          formattedError.type = 'NOT_FOUND_ERROR';
          if (useGenericUnlessServerMessage) {
            formattedError.message = 'The requested resource was not found.';
          }
        } else if (error.response.status === 422) {
          formattedError.type = 'VALIDATION_ERROR';
          if (useGenericUnlessServerMessage) {
            formattedError.message = 'Validation failed. Please check your input.';
          }
        } else {
          formattedError.type = 'CLIENT_ERROR';
        }
      } else if (error.response.status >= 500) {
        formattedError.type = 'SERVER_ERROR';
        if (useGenericUnlessServerMessage) {
          formattedError.message = 'An error occurred on the server. Please try again later.';
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      formattedError.type = 'NETWORK_ERROR';
      formattedError.message = 'Network error. Please check your internet connection.';
    } else if (error.message) {
      // Something happened in setting up the request that triggered an Error
      formattedError.message = error.message;
      
      // Check for specific error messages
      if (error.message.includes('timeout')) {
        formattedError.type = 'TIMEOUT_ERROR';
        formattedError.message = 'Request timed out. Please try again.';
      } else if (error.message.includes('Network Error')) {
        formattedError.type = 'NETWORK_ERROR';
        formattedError.message = 'Network error. Please check your internet connection.';
      }
    }
  
    return formattedError;
  };
  
  /**
   * Gets a user-friendly error message based on error type and status code
   * @param {Object} error - Formatted error object from formatApiError
   * @returns {string} User-friendly error message
   */
  export const getUserFriendlyErrorMessage = (error) => {
    // Use default error message from formatted error
    if (!error || !error.type) {
      return 'An unexpected error occurred. Please try again later.';
    }
  
    // Return user-friendly messages based on error type
    switch (error.type) {
      case 'AUTHENTICATION_ERROR':
        return 'Your session has expired. Please log in again.';
      case 'AUTHORIZATION_ERROR':
        return 'You don\'t have permission to perform this action.';
      case 'VALIDATION_ERROR':
        return 'Please check your input and try again.';
      case 'NOT_FOUND_ERROR':
        return 'The requested information could not be found.';
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'TIMEOUT_ERROR':
        return 'The server took too long to respond. Please try again later.';
      case 'SERVER_ERROR':
        return 'The server encountered an error. Our team has been notified.';
      default:
        return error.message || 'An unexpected error occurred. Please try again later.';
    }
  };
  
  /**
   * Process form validation errors to be used with form libraries
   * @param {Object} error - Error object from API response
   * @returns {Object} Form errors object with field names as keys and error messages as values
   */
  export const processFormErrors = (error) => {
    const formErrors = {};
    
    if (!error || !error.details) {
      return formErrors;
    }
    
    // Handle common validation error formats
    if (error.details.errors && typeof error.details.errors === 'object') {
      // Format: { errors: { field1: ['Error message'], field2: ['Error message'] } }
      Object.entries(error.details.errors).forEach(([field, messages]) => {
        formErrors[field] = Array.isArray(messages) ? messages[0] : messages;
      });
    } else if (error.details.fields && Array.isArray(error.details.fields)) {
      // Format: { fields: [{ field: 'field1', message: 'Error message' }] }
      error.details.fields.forEach(fieldError => {
        if (fieldError.field && fieldError.message) {
          formErrors[fieldError.field] = fieldError.message;
        }
      });
    } else if (error.details.validationErrors && Array.isArray(error.details.validationErrors)) {
      // Format: { validationErrors: [{ path: 'field1', message: 'Error message' }] }
      error.details.validationErrors.forEach(validationError => {
        if (validationError.path && validationError.message) {
          formErrors[validationError.path] = validationError.message;
        }
      });
    }
    
    return formErrors;
  };
  
  export default {
    formatApiError,
    getHttpStatusFromApiError,
    getApiErrorMessageFromCatch,
    getUserFriendlyErrorMessage,
    processFormErrors
  };